import React, { useEffect, useState } from "react";
import { fetchCandidates } from "../../redux/candidateSlice";
import { fetchJobs } from "../../redux/jobsSlice";
import { 
  fetchInterviews, 
  fetchInterviewsByCandidate,
  scheduleInterview,
  updateInterviewStatus,
  hireCandidate,
  createInterviewsForCandidate
} from "../../redux/interviewSlice";
import { useDispatch, useSelector } from "react-redux";
import InterviewScheduleModal from "../../modals/Hrms/InterviewScheduleModal";
import InterviewStatusModal from "../../modals/Hrms/InterviewStatusModal";

const Interviews = () => {
  const dispatch = useDispatch();
  const { candidates } = useSelector((state) => state.candidates);
  const { jobs } = useSelector((state) => state.jobs);
  const { candidateInterviews, loading } = useSelector((state) => state.interviews);

  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [currentStage, setCurrentStage] = useState(null);

  const [isScheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);

  // Filter states
  const [nameFilter, setNameFilter] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [firstInterviewFilter, setFirstInterviewFilter] = useState("");
  const [secondInterviewFilter, setSecondInterviewFilter] = useState("");
  const [finalInterviewFilter, setFinalInterviewFilter] = useState("");

  useEffect(() => {
    dispatch(fetchCandidates());
    dispatch(fetchJobs());
    dispatch(fetchInterviews());
  }, [dispatch]);

  // Get interview by stage for a candidate
  const getInterviewByStage = (candidateId, stage) => {
    if (!candidateInterviews[candidateId]) return null;
    return candidateInterviews[candidateId].find(interview => interview.stage === stage);
  };

  // Get status of a specific stage for a candidate
  const getStageStatus = (candidateId, stage) => {
    const interview = getInterviewByStage(candidateId, stage);
    return interview ? interview.status : "Not Scheduled";
  };

  // Get job title for a candidate
  const getJobTitle = (candidate) => {
    if (candidate.jobId) {
      const job = jobs.find(job => job.id === candidate.jobId);
      return job ? job.PostTitle : candidate.currentDesignation || "N/A";
    }
    return candidate.currentDesignation || "N/A";
  };

  // Filter candidates to only show approved ones and apply search/filter criteria
  const approvedCandidates = candidates.filter(candidate => {
    if (candidate.candidateStatus !== 'Approved') return false;
    
    // Name filter
    if (nameFilter && !candidate.name.toLowerCase().includes(nameFilter.toLowerCase())) {
      return false;
    }
    
    // Designation filter
    if (designationFilter && designationFilter !== "Any" && 
        !getJobTitle(candidate).toLowerCase().includes(designationFilter.toLowerCase())) {
      return false;
    }
    
    // Interview status filters
    const firstStatus = getStageStatus(candidate.id, 'first');
    const secondStatus = getStageStatus(candidate.id, 'second');
    const finalStatus = getStageStatus(candidate.id, 'final');
    
    if (firstInterviewFilter && firstInterviewFilter !== "1st Interview Status" && 
        firstStatus !== firstInterviewFilter) {
      return false;
    }
    
    if (secondInterviewFilter && secondInterviewFilter !== "2nd Interview Status" && 
        secondStatus !== secondInterviewFilter) {
      return false;
    }
    
    if (finalInterviewFilter && finalInterviewFilter !== "Final Interview Status" && 
        finalStatus !== finalInterviewFilter) {
      return false;
    }
    
    return true;
  });

  // Fetch interviews for each approved candidate
  useEffect(() => {
    approvedCandidates.forEach(candidate => {
      if (!candidateInterviews[candidate.id]) {
        dispatch(fetchInterviewsByCandidate(candidate.id));
      }
    });
  }, [approvedCandidates, candidateInterviews, dispatch]);

  // Open the correct modal based on interview stage and status
  const openModal = (candidate, stage) => {
    // Check if this stage can be scheduled
    if (!canScheduleStage(candidate.id, stage)) {
      alert(`Cannot schedule ${getStageName(stage)}. Previous stage must be passed first.`);
      return;
    }

    setSelectedCandidate(candidate);
    setCurrentStage(stage);

    const stageStatus = getStageStatus(candidate.id, stage);
    if (stageStatus === "Scheduled" || stageStatus === "Passed" || stageStatus === "Failed") {
      setUpdateModalOpen(true);
    } else {
      setScheduleModalOpen(true);
    }
  };

  const closeScheduleModal = () => {
    setScheduleModalOpen(false);
    setSelectedCandidate(null);
    setCurrentStage(null);
  };

  const closeUpdateModal = () => {
    setUpdateModalOpen(false);
    setSelectedCandidate(null);
    setCurrentStage(null);
  };

  // Handle interview scheduling
  const handleScheduleInterview = async (candidateId, scheduleData) => {
    let interview = getInterviewByStage(candidateId, currentStage);
    
    // If interview doesn't exist, create it first
    if (!interview) {
      console.log("Interview doesn't exist, creating it first...");
      await dispatch(createInterviewsForCandidate(candidateId));
      // Wait a bit for the interviews to be created
      await new Promise(resolve => setTimeout(resolve, 1000));
      interview = getInterviewByStage(candidateId, currentStage);
    }
    
    if (interview) {
      dispatch(scheduleInterview({
        id: interview.id,
        data: {
          interviewer: scheduleData.interviewer,
          location: scheduleData.location,
          date: scheduleData.date,
          startTime: scheduleData.startTime,
          endTime: scheduleData.endTime
        }
      }));
    } else {
      console.error("Failed to create or find interview for candidate:", candidateId, "stage:", currentStage);
    }
  };

  // Handle updating interview status
  const handleUpdateStatus = (candidateId, newStatus) => {
    const interview = getInterviewByStage(candidateId, currentStage);
    if (interview) {
      dispatch(updateInterviewStatus({
        id: interview.id,
        status: newStatus
      }));
    }
  };

  // Function to get the overall candidate status for hire column
  const getCandidateHireStatus = (candidateId) => {
    if (!candidateInterviews[candidateId]) return "Not Started";
    
    const firstStatus = getStageStatus(candidateId, 'first');
    const secondStatus = getStageStatus(candidateId, 'second');
    const finalStatus = getStageStatus(candidateId, 'final');
    
    // If any stage is failed, candidate is rejected
    if (firstStatus === "Failed" || secondStatus === "Failed" || finalStatus === "Failed") {
      return "Rejected";
    }
    
    // If all stages are passed, candidate is eligible for hiring
    if (firstStatus === "Passed" && secondStatus === "Passed" && finalStatus === "Passed") {
      return "Eligible for Hire";
    }
    
    // If candidate is in progress (some stages passed, none failed)
    if (firstStatus === "Passed" || secondStatus === "Passed" || finalStatus === "Passed") {
      return "In Progress";
    }
    
    return "Not Started";
  };

  // Function to check if a stage can be scheduled (previous stage must be passed)
  const canScheduleStage = (candidateId, stage) => {
    if (!candidateInterviews[candidateId]) return stage === 'first';
    
    const stageOrder = ['first', 'second', 'final'];
    const currentStageIndex = stageOrder.indexOf(stage);
    
    // First stage can always be scheduled
    if (currentStageIndex === 0) return true;
    
    // For subsequent stages, previous stage must be passed
    const previousStage = stageOrder[currentStageIndex - 1];
    return getStageStatus(candidateId, previousStage) === "Passed";
  };

  // Function to hire candidate
  const handleHireCandidate = (candidateId) => {
    dispatch(hireCandidate(candidateId));
  };

  // Get stage display name for UI
  const getStageName = (stage) => {
    const stageNames = {
      first: "1st Interview",
      second: "2nd Interview", 
      final: "Final Interview"
    };
    return stageNames[stage] || stage;
  };

  if (loading) {
    return <div className="p-8 text-center">Loading interviews data...</div>;
  }

  if (approvedCandidates.length === 0) {
    return (
      <div className="p-8 bg-gray-100 min-h-screen">
        <div className="bg-white p-6 rounded-lg shadow-md text-center">
          <h2 className="text-2xl font-semibold mb-4">Interviews</h2>
          <p className="text-gray-600">
            No approved candidates available for interviews. 
            <br />
            Candidates need to be approved in the Candidates page before they can be scheduled for interviews.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-100 min-h-screen">
      <div className="bg-white p-6 rounded-lg shadow-md overflow-x-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold">Search & Filter</h2>
          <button
            onClick={() => {
              setNameFilter("");
              setDesignationFilter("");
              setFirstInterviewFilter("");
              setSecondInterviewFilter("");
              setFinalInterviewFilter("");
            }}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            Clear Filters
          </button>
        </div>
        <div className="grid grid-cols-6 gap-4 mb-6">
          <input
            type="text"
            placeholder="First name"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            className="col-span-1 p-2 border rounded-md"
          />
          <select 
            className="col-span-1 p-2 border rounded-md"
            value={designationFilter}
            onChange={(e) => setDesignationFilter(e.target.value)}
          >
            <option value="">Any</option>
            {jobs.map((job) => (
              <option key={job.id} value={job.PostTitle}>
                {job.PostTitle}
              </option>
            ))}
          </select>
          <select 
            className="col-span-1 p-2 border rounded-md cursor-pointer"
            value={firstInterviewFilter}
            onChange={(e) => setFirstInterviewFilter(e.target.value)}
          >
            <option value="">1st Interview Status</option>
            <option value="Not Scheduled">Not Scheduled</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Passed">Passed</option>
            <option value="Failed">Failed</option>
          </select>
          <select 
            className="col-span-1 p-2 border rounded-md cursor-pointer"
            value={secondInterviewFilter}
            onChange={(e) => setSecondInterviewFilter(e.target.value)}
          >
            <option value="">2nd Interview Status</option>
            <option value="Not Scheduled">Not Scheduled</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Passed">Passed</option>
            <option value="Failed">Failed</option>
          </select>
          <select 
            className="col-span-1 p-2 border rounded-md cursor-pointer"
            value={finalInterviewFilter}
            onChange={(e) => setFinalInterviewFilter(e.target.value)}
          >
            <option value="">Final Interview Status</option>
            <option value="Not Scheduled">Not Scheduled</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Passed">Passed</option>
            <option value="Failed">Failed</option>
          </select>
        </div>

        <table className="table-auto w-full border-collapse">
          <thead className="bg-gray-200 text-gray-600">
            <tr>
              <th className="p-2">Applicant Name</th>
              <th className="p-2">Post Applied</th>
              <th className="p-2">1st Interview Status</th>
              <th className="p-2">2nd Interview Status</th>
              <th className="p-2">Final Interview Status</th>
              <th className="p-2">Hire</th>
              <th className="p-2">Rating</th>
              <th className="p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {approvedCandidates.map((candidate) => (
              <tr key={candidate.id} className="border-b">
                <td className="p-2">{candidate.name}</td>
                <td className="p-2">{getJobTitle(candidate)}</td>
                {["first", "second", "final"].map((stage) => {
                  const stageStatus = getStageStatus(candidate.id, stage);
                  const canSchedule = canScheduleStage(candidate.id, stage);
                  
                  return (
                    <td
                      key={stage}
                      className={`p-2 ${
                        canSchedule ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                      } ${
                        stageStatus === "Scheduled"
                          ? "text-green-600"
                          : stageStatus === "Passed"
                          ? "text-blue-600"
                          : stageStatus === "Failed"
                          ? "text-red-600"
                          : canSchedule
                          ? "text-gray-600 hover:bg-gray-100"
                          : "text-gray-400"
                      }`}
                      onClick={() => canSchedule && openModal(candidate, stage)}
                      title={!canSchedule ? "Previous stage must be passed first" : ""}
                    >
                      {stageStatus}
                    </td>
                  );
                })}
                <td className="p-2">
                  {(() => {
                    const hireStatus = getCandidateHireStatus(candidate.id);
                    
                    if (hireStatus === "Eligible for Hire") {
                      return (
                        <button
                          className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
                          onClick={() => handleHireCandidate(candidate.id)}
                        >
                          Hire
                        </button>
                      );
                    } else if (hireStatus === "Rejected") {
                      return (
                        <span className="px-4 py-2 rounded-md bg-red-100 text-red-800 text-sm font-medium">
                          Rejected
                        </span>
                      );
                    } else if (hireStatus === "In Progress") {
                      return (
                        <span className="px-4 py-2 rounded-md bg-yellow-100 text-yellow-800 text-sm font-medium">
                          In Progress
                        </span>
                      );
                    } else {
                      return (
                        <span className="px-4 py-2 rounded-md bg-gray-100 text-gray-600 text-sm font-medium">
                          Not Started
                        </span>
                      );
                    }
                  })()}
                </td>
                <td className="p-2">{candidate.rating ? "⭐".repeat(Math.round(candidate.rating)) : ""}</td>
                <td className="p-2">...</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Interview Scheduling Modal */}
      <InterviewScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={closeScheduleModal}
        candidate={selectedCandidate}
        stageName={getStageName(currentStage)}
        stage={currentStage}
        onSchedule={handleScheduleInterview}
      />

      {/* Update Interview Status Modal */}
      <InterviewStatusModal
        isOpen={isUpdateModalOpen}
        onClose={closeUpdateModal}
        candidate={selectedCandidate}
        stageName={getStageName(currentStage)}
        onUpdateStatus={handleUpdateStatus}
      />
    </div>
  );
};

export default Interviews;